import React, { useState, useRef, useEffect } from 'react';
import { Settings, Share2, Lock, User, Tags, X } from 'lucide-react';
import { Button, TextInput, Popover, Text, Badge } from '@mantine/core';
import { TodoList } from '../types/todo';
import { AssigneeDropdown } from './AssigneeDropdown';
import { SearchFilter } from './SearchFilter';
import { useTodoLists, useTodos } from '../hooks/useTodos';
import { useUser } from '../contexts/UserContext';
import { BaseDropdown } from './BaseDropdown';
import { StatsChart } from './StatsChart';
import { getAllUserTags } from '../utils/tagUtils';
import './FilterBar-clean.css';

interface FilterBarProps {
  onFilterChange: (filters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => void;
  currentFilters?: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string };
  selectedList?: TodoList | null;
  onCreateList?: () => void;
  onEditList?: () => void;
  onInviteUser?: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  onFilterChange,
  currentFilters,
  selectedList,
  onCreateList,
  onEditList,
  onInviteUser: _onInviteUser
}) => {
  // const { currentUser } = useUser(); // Not needed for filtering
  // const { data: todoLists = [] } = useTodoLists(); // Lists accessed via ListDropdown

  const [isListDropdownOpen, setIsListDropdownOpen] = useState(false);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [isMobileTagsDropdownOpen, setIsMobileTagsDropdownOpen] = useState(false);
  const [tagSearchValue, setTagSearchValue] = useState('');
  const [mobileTagSearchValue, setMobileTagSearchValue] = useState('');
  const [selectedTagIndex, setSelectedTagIndex] = useState(0);
  const [mobileSelectedTagIndex, setMobileSelectedTagIndex] = useState(0);
  const tagsSelectRef = useRef<HTMLInputElement>(null);
  const mobileTagsSelectRef = useRef<HTMLInputElement>(null);
  const tagsControlRef = useRef<HTMLDivElement>(null);
  const mobileTagsControlRef = useRef<HTMLDivElement>(null);
  const { data: todoLists = [] } = useTodoLists();
  const { data: todos = [] } = useTodos();
  const { currentUser } = useUser();

  // Helper function to check if current user owns a list
  const isOwner = (list: TodoList) => {
    return currentUser?.email === list.createdBy;
  };

  // Get all available tags for the filter dropdown
  const allTags = getAllUserTags(todos);
  
  // Filter tags based on search
  const filteredTags = allTags.filter(tag => 
    tag.toLowerCase().includes(tagSearchValue.toLowerCase()) &&
    !(currentFilters?.tags || []).includes(tag)
  );

  // Filter tags for mobile based on mobile search
  const mobileFilteredTags = allTags.filter(tag => 
    tag.toLowerCase().includes(mobileTagSearchValue.toLowerCase()) &&
    !(currentFilters?.tags || []).includes(tag)
  );

  // Filter todos based on current filters to show relevant stats
  const filteredTodos = todos.filter(todo => {
    // Apply list filter
    if (currentFilters?.list && currentFilters.list !== 'all') {
      if (typeof currentFilters.list === 'number' && todo.listId !== currentFilters.list) {
        return false;
      }
    }
    
    // Apply assignee filter
    if (currentFilters?.assignee && currentFilters.assignee !== 'any') {
      if (currentFilters.assignee === 'unassigned') {
        if (todo.assignedTo && todo.assignedTo.trim() !== '') {
          return false;
        }
      } else {
        if (!todo.assignedTo || todo.assignedTo !== currentFilters.assignee) {
          return false;
        }
      }
    }
    
    // Apply tags filter
    if (currentFilters?.tags && currentFilters.tags.length > 0) {
      let todoTags: string[] = [];
      if (Array.isArray(todo.tags)) {
        todoTags = todo.tags;
      } else if (typeof todo.tags === 'string' && todo.tags) {
        todoTags = (todo.tags as string).split(',').map((t: string) => t.trim().toLowerCase());
      }
      
      // Check if todo has at least one of the selected tags
      const hasMatchingTag = currentFilters.tags.some(filterTag => 
        todoTags.includes(filterTag.toLowerCase())
      );
      
      if (!hasMatchingTag) {
        return false;
      }
    }
    
    return true;
  });

  const handleAssigneeChange = (value: string | undefined) => {
    let newAssignee: string;
    if (value === '__unassigned__') {
      newAssignee = 'unassigned';
    } else if (value === undefined || value === '' || value === null) {
      newAssignee = 'any';
    } else {
      newAssignee = value;
    }
    onFilterChange({ 
      assignee: newAssignee, 
      list: currentFilters?.list || 'all', 
      completed: currentFilters?.completed || 'hidden',
      tags: currentFilters?.tags || [],
      search: currentFilters?.search || ''
    });
  };

  const handleListChange = (listId: number | undefined) => {
    onFilterChange({
      assignee: currentFilters?.assignee || 'any',
      list: listId || 'all',
      completed: currentFilters?.completed || 'hidden',
      tags: currentFilters?.tags || [],
      search: currentFilters?.search || ''
    });
    setIsListDropdownOpen(false);
  };

  const handleTagsChange = (selectedTags: string[]) => {
    onFilterChange({
      assignee: currentFilters?.assignee || 'any',
      list: currentFilters?.list || 'all',
      completed: currentFilters?.completed || 'hidden',
      tags: selectedTags,
      search: currentFilters?.search || ''
    });
  };

  const handleSearchChange = (searchValue: string) => {
    onFilterChange({
      assignee: currentFilters?.assignee || 'any',
      list: currentFilters?.list || 'all',
      completed: currentFilters?.completed || 'hidden',
      tags: currentFilters?.tags || [],
      search: searchValue
    });
  };

  const handleAddTag = (tag: string) => {
    const currentTags = currentFilters?.tags || [];
    if (!currentTags.includes(tag) && currentTags.length < 5) {
      handleTagsChange([...currentTags, tag]);
    }
    setTagSearchValue('');
  };

  const handleRemoveTag = (tag: string) => {
    const currentTags = currentFilters?.tags || [];
    handleTagsChange(currentTags.filter(t => t !== tag));
  };

  const handleTagInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (filteredTags.length > 0 && selectedTagIndex >= 0 && selectedTagIndex < filteredTags.length) {
        handleAddTag(filteredTags[selectedTagIndex]);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedTagIndex(prev => Math.min(prev + 1, filteredTags.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedTagIndex(prev => Math.max(prev - 1, 0));
    }
  };

  // Mobile tag handlers
  const handleMobileAddTag = (tag: string) => {
    const currentTags = currentFilters?.tags || [];
    if (!currentTags.includes(tag) && currentTags.length < 5) {
      handleTagsChange([...currentTags, tag]);
    }
    setMobileTagSearchValue('');
  };

  const handleMobileTagInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (filteredTags.length > 0 && mobileSelectedTagIndex >= 0 && mobileSelectedTagIndex < filteredTags.length) {
        handleMobileAddTag(filteredTags[mobileSelectedTagIndex]);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMobileSelectedTagIndex(prev => Math.min(prev + 1, filteredTags.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMobileSelectedTagIndex(prev => Math.max(prev - 1, 0));
    }
  };

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedTagIndex(0);
  }, [tagSearchValue]);

  useEffect(() => {
    setMobileSelectedTagIndex(0);
  }, [mobileTagSearchValue]);

  const handleListSelect = (listId: number) => {
    handleListChange(listId);
  };

  const handleUnselectList = () => {
    handleListChange(undefined);
  };



  return (
    <div className="fb-root css-reset--component-isolation">
      <div className="fb-container">
        <div className="fb-content">
          <div className="fb-main">
            {/* Accent bar */}
            {selectedList && (
              <div className="fb-accent" style={{ backgroundColor: selectedList.color || '#ef4444' }} />
            )}
            
            {/* Title row */}
            <div className="fb-title-row">
              <div className="fb-title-section">
                {/* List Title as Dropdown */}
                <BaseDropdown
                  trigger={
                    <button className="fb-list-dropdown-trigger css-reset">
                      <span className="fb-list-title">
                        {selectedList?.name || 'All lists'}
                      </span>
                    </button>
                  }
                  isOpen={isListDropdownOpen}
                  onToggle={setIsListDropdownOpen}
                  className="fb-list-dropdown"
                  contentClassName="fb-list-dropdown-content"
                  maxHeight={500}
                >
                  <div className="fb-list-dropdown-options">
                    {/* All lists option */}
                    <button
                      type="button"
                      className={`fb-list-option css-reset ${!currentFilters?.list || currentFilters?.list === 'all' ? 'fb-list-option--selected' : ''}`}
                      onClick={handleUnselectList}
                    >
                      <div className="fb-list-option-icon" style={{ backgroundColor: '#64748b' }}>
                        <span>AL</span>
                      </div>
                      <div className="fb-list-option-details">
                        <span className="fb-list-option-name">All lists</span>
                        <span className="fb-list-option-desc">Viewing todos from all lists</span>
                      </div>
                    </button>

                    {/* Individual lists */}
                    {todoLists.map((list) => {
                      const isSelected = currentFilters?.list === list.id;
                      return (
                        <button
                          key={list.id}
                          type="button"
                          className={`fb-list-option css-reset ${isSelected ? 'fb-list-option--selected' : ''}`}
                          onClick={() => handleListSelect(list.id)}
                        >
                          <div className="fb-list-option-icon" style={{ backgroundColor: list.color || '#3b82f6' }}>
                            <span>{list.name.charAt(0).toUpperCase()}</span>
                          </div>
                          <div className="fb-list-option-details">
                            <span className="fb-list-option-name">{list.name}</span>
                            {list.description && (
                              <span className="fb-list-option-desc">{list.description}</span>
                            )}
                          </div>
                          <div className="fb-list-ownership">
                            {isOwner(list) ? (
                              <div className="fb-ownership-badge" title="You own this list">
                                <User size={12} />
                              </div>
                            ) : (
                              <div className="fb-ownership-badge" title={`Shared by ${list.createdBy}`}>
                                <Share2 size={12} />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}

                    {/* Create new list */}
                    {onCreateList && (
                      <button
                        type="button"
                        className="fb-list-option fb-list-option--create css-reset"
                        onClick={() => {
                          setIsListDropdownOpen(false);
                          onCreateList();
                        }}
                      >
                        <span className="fb-list-option-name">+ Create new list</span>
                      </button>
                    )}
                  </div>
                </BaseDropdown>
              </div>

              {/* Desktop: Controls in title row, Mobile: Only settings */}
              <div className="fb-controls">
                <div className="fb-assignee-control">
                  <AssigneeDropdown
                    value={currentFilters?.assignee === 'unassigned' ? '__unassigned__' : (currentFilters?.assignee !== 'any' ? currentFilters?.assignee : undefined)}
                    onChange={handleAssigneeChange}
                    placeholder="any"
                    variant="inline"
                  />
                </div>

                <div className="fb-search-control">
                  <SearchFilter
                    value={currentFilters?.search || ''}
                    onChange={handleSearchChange}
                  />
                </div>

                <div className="fb-tags-control" ref={tagsControlRef}>
                  <Popover
                    opened={isTagsDropdownOpen}
                    onDismiss={() => setIsTagsDropdownOpen(false)}
                    position="bottom-start"
                    withArrow
                    shadow="md"
                    radius="md"
                    offset={4}
                    trapFocus={false}
                    returnFocus={false}
                  >
                    <Popover.Target>
                      <Button
                        variant="default"
                        size="compact-sm"
                        onClick={() => {
                          setIsTagsDropdownOpen(!isTagsDropdownOpen);
                          // Focus the input after the popover opens
                          if (!isTagsDropdownOpen) {
                            setTagSearchValue('');
                            setSelectedTagIndex(0);
                            setTimeout(() => {
                              if (tagsSelectRef.current) {
                                tagsSelectRef.current.focus();
                              }
                            }, 100);
                          }
                        }}
                      >
                        {currentFilters?.tags && currentFilters.tags.length > 0 ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Tags size={12} />
                            <Text size="xs" style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {currentFilters.tags.length === 1 
                                ? currentFilters.tags[0] 
                                : `${currentFilters.tags.length} tags`}
                            </Text>
                            <X 
                              size={12} 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTagsChange([]);
                              }}
                              style={{ cursor: 'pointer', opacity: 0.7 }}
                            />
                          </span>
                        ) : (
                          <Tags size={12} />
                        )}
                      </Button>
                    </Popover.Target>
                    <Popover.Dropdown p={0} style={{ border: 'none' }}>
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          width: '280px', 
                          padding: '16px',
                          backgroundColor: 'var(--app-surface)',
                          border: '1px solid var(--app-border)',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                        {/* Selected Tags */}
                        {currentFilters?.tags && currentFilters.tags.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {currentFilters.tags.map((tag) => (
                                <Badge
                                  key={tag}
                                  variant="light"
                                  color="blue"
                                  size="sm"
                                  style={{ 
                                    fontSize: '11px',
                                    height: '24px',
                                    cursor: 'pointer'
                                  }}
                                  rightSection={
                                    <X 
                                      size={12} 
                                      onClick={() => handleRemoveTag(tag)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  }
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Search Input */}
                        <TextInput
                          ref={tagsSelectRef}
                          value={tagSearchValue}
                          onChange={(e) => setTagSearchValue(e.target.value)}
                          onKeyDown={handleTagInputKeyDown}
                          placeholder="Type to search..."
                          size="sm"
                          disabled={currentFilters?.tags && currentFilters.tags.length >= 5}
                          styles={{
                            input: {
                              border: 'none',
                              backgroundColor: 'var(--app-bg)',
                              fontSize: '13px',
                            }
                          }}
                        />
                        
                        {/* Max tags hint */}
                        {currentFilters?.tags && currentFilters.tags.length >= 5 && (
                          <Text size="xs" c="dimmed" style={{ marginTop: '8px', fontStyle: 'italic' }}>
                            Maximum of 5 tags selected
                          </Text>
                        )}

                        {/* Available Tags List */}
                        {filteredTags.length > 0 && !(currentFilters?.tags && currentFilters.tags.length >= 5) && (
                          <div style={{ 
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: '1px solid var(--app-border)',
                            maxHeight: '160px',
                            overflowY: 'auto'
                          }}>
                            {filteredTags.map((tag, index) => (
                              <div
                                key={tag}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleAddTag(tag);
                                }}
                                onMouseEnter={() => setSelectedTagIndex(index)}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  color: index === selectedTagIndex ? 'var(--mantine-color-blue-7)' : 'var(--app-text-primary)',
                                  backgroundColor: index === selectedTagIndex ? 'var(--mantine-color-blue-1)' : 'transparent',
                                  border: index === selectedTagIndex ? '1px solid var(--mantine-color-blue-3)' : '1px solid transparent',
                                  fontWeight: index === selectedTagIndex ? 500 : 400,
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                {tag}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Popover.Dropdown>
                  </Popover>
                </div>
                
                {selectedList && onEditList && (
                  <Button
                    variant="default"
                    size="compact-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (typeof onEditList === 'function') {
                        onEditList();
                      }
                    }}
                    title={`Edit ${selectedList.name} list`}
                    px={8}
                  >
                    <Settings size={12} />
                  </Button>
                )}
              </div>
            </div>

            {/* Mobile controls row */}
            <div className="fb-mobile-controls">
              <div className="fb-assignee-control">
                <AssigneeDropdown
                  value={currentFilters?.assignee === 'unassigned' ? '__unassigned__' : (currentFilters?.assignee !== 'any' ? currentFilters?.assignee : undefined)}
                  onChange={handleAssigneeChange}
                  placeholder="any"
                  variant="inline"
                />
              </div>

              <div className="fb-search-control">
                <SearchFilter
                  value={currentFilters?.search || ''}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="fb-tags-control" ref={mobileTagsControlRef}>
                <Popover
                  opened={isMobileTagsDropdownOpen}
                  onDismiss={() => setIsMobileTagsDropdownOpen(false)}
                  position="bottom-start"
                  withArrow
                  shadow="md"
                  radius="md"
                  offset={4}
                  trapFocus={false}
                  returnFocus={false}
                  closeOnClickOutside={true}
                >
                  <Popover.Target>
                    <Button
                      variant="default"
                      size="compact-sm"
                      onClick={() => {
                        setIsMobileTagsDropdownOpen(!isMobileTagsDropdownOpen);
                        // Focus the input after the popover opens
                        if (!isMobileTagsDropdownOpen) {
                          setMobileTagSearchValue('');
                          setMobileSelectedTagIndex(0);
                          setTimeout(() => {
                            if (mobileTagsSelectRef.current) {
                              mobileTagsSelectRef.current.focus();
                            }
                          }, 100);
                        }
                      }}
                    >
                      {currentFilters?.tags && currentFilters.tags.length > 0 ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Tags size={12} />
                          <Text size="xs" style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {currentFilters.tags.length === 1 
                              ? currentFilters.tags[0] 
                              : `${currentFilters.tags.length} tags`}
                          </Text>
                          <X 
                            size={12} 
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleTagsChange([]);
                            }}
                            style={{ cursor: 'pointer', opacity: 0.7 }}
                          />
                        </span>
                      ) : (
                        <Tags size={12} />
                      )}
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown p={0} style={{ border: 'none' }}>
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        width: '280px', 
                        padding: '16px',
                        backgroundColor: 'var(--app-surface)',
                        border: '1px solid var(--app-border)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}>
                      {/* Selected Tags */}
                      {currentFilters?.tags && currentFilters.tags.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {currentFilters.tags.map((tag) => (
                              <Badge
                                key={tag}
                                variant="light"
                                color="blue"
                                size="sm"
                                style={{ 
                                  fontSize: '11px',
                                  height: '24px',
                                  cursor: 'pointer'
                                }}
                                rightSection={
                                  <X 
                                    size={12} 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleRemoveTag(tag);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  />
                                }
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Search Input */}
                      <TextInput
                        ref={mobileTagsSelectRef}
                        value={mobileTagSearchValue}
                        onChange={(e) => setMobileTagSearchValue(e.target.value)}
                        onKeyDown={handleMobileTagInputKeyDown}
                        placeholder="Type to search..."
                        size="sm"
                        disabled={currentFilters?.tags && currentFilters.tags.length >= 5}
                        styles={{
                          input: {
                            border: 'none',
                            backgroundColor: 'var(--app-bg)',
                            fontSize: '13px',
                          }
                        }}
                      />
                      
                      {/* Max tags hint */}
                      {currentFilters?.tags && currentFilters.tags.length >= 5 && (
                        <Text size="xs" c="dimmed" style={{ marginTop: '8px', fontStyle: 'italic' }}>
                          Maximum of 5 tags selected
                        </Text>
                      )}

                      {/* Available Tags List */}
                      {mobileFilteredTags.length > 0 && !(currentFilters?.tags && currentFilters.tags.length >= 5) && (
                        <div style={{ 
                          marginTop: '12px',
                          paddingTop: '12px',
                          borderTop: '1px solid var(--app-border)',
                          maxHeight: '160px',
                          overflowY: 'auto'
                        }}>
                          {mobileFilteredTags.map((tag, index) => (
                            <div
                              key={tag}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMobileAddTag(tag);
                              }}
                              onMouseEnter={() => setMobileSelectedTagIndex(index)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: index === mobileSelectedTagIndex ? 'var(--mantine-color-blue-7)' : 'var(--app-text-primary)',
                                backgroundColor: index === mobileSelectedTagIndex ? 'var(--mantine-color-blue-1)' : 'transparent',
                                border: index === mobileSelectedTagIndex ? '1px solid var(--mantine-color-blue-3)' : '1px solid transparent',
                                fontWeight: index === mobileSelectedTagIndex ? 500 : 400,
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {tag}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Popover.Dropdown>
                </Popover>
              </div>
            </div>
            
            {/* Description and stats row */}
            <div className="fb-content-row">
              <div className="fb-description-section">
                {/* Sharing indicator positioned next to description */}
                {selectedList && (
                  <div className="fb-sharing-indicator">
                    {selectedList.isShared ? (
                      <div className="fb-sharing-badge fb-sharing-badge--shared" title={`Shared with: ${selectedList.sharedWith?.join(', ') || 'No users'}`}>
                        <Share2 size={12} />
                        <span>{selectedList.sharedWith?.length || 0}</span>
                      </div>
                    ) : (
                      <div className="fb-sharing-badge fb-sharing-badge--private" title="Private list">
                        <Lock size={12} />
                      </div>
                    )}
                  </div>
                )}
                
                {/* List description */}
                <div className="fb-description">
                  {selectedList?.description ? (
                    selectedList.description
                  ) : (!currentFilters?.list || currentFilters?.list === 'all') ? (
                    'Viewing todos from all lists'
                  ) : null}
                </div>
              </div>
              
              {/* Statistics section - fully right aligned */}
              <div className="fb-stats">
                <StatsChart todos={filteredTodos}>
                  <div className="fb-context-stats">
                    <div className="fb-stats-mini-chart">
                      {/* Simple 7-day completion chart */}
                      {Array.from({ length: 7 }, (_, i) => {
                        const date = new Date();
                        date.setDate(date.getDate() - (6 - i));
                        const dayTodos = filteredTodos.filter(todo => {
                          if (!todo.completedAt) return false;
                          const completedDate = new Date(todo.completedAt);
                          const completedDay = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
                          const currentDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                          return completedDay.getTime() === currentDay.getTime();
                        });
                        const height = dayTodos.length === 0 ? 2 : Math.max(2, Math.min(12, dayTodos.length * 3));
                        return (
                          <div
                            key={i}
                            className="fb-mini-bar"
                            style={{
                              height: `${height}px`,
                              backgroundColor: dayTodos.length === 0 ? '#e2e8f0' : '#3b82f6'
                            }}
                          />
                        );
                      })}
                    </div>
                    <span className="fb-stats-text">
                      {filteredTodos.filter(t => t.isCompleted).length}/{filteredTodos.length} completed
                    </span>
                  </div>
                </StatsChart>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};